import type {QueryClient} from '@github-ui/react-query'
import {type JSONRequestInit, verifiedFetchJSON} from '@github-ui/verified-fetch'

import {LABELS, VALUES} from '../constants'
import {parseFetchError, savedViewPinsQueryKey, savedViewQueryKey, savedViewsQueryKey} from '../queries'
import type {SavedView, SavedViewId, SavedViewsResponse, SetSavedViewPinnedVariables} from '../types'
import {viewVisibility} from './views-visibility'

export interface SavedViewMutationOptions {
  owner: string
  repo: string
}

interface SavedViewCacheTargets extends SavedViewMutationOptions {
  queryClient: QueryClient
  basePath?: string
  listUrl: string
}

interface SavedViewDetailCache {
  savedView: SavedView
  viewerCanManageViews: boolean
}

function preserveViewId(previous: SavedView | undefined, savedView: SavedView): SavedView {
  if (savedView.viewId !== undefined || previous?.viewId === undefined) return savedView

  return {...savedView, viewId: previous.viewId}
}

export const PINNED_LIMIT_EXCEEDED = 'pinned_limit_exceeded'

export class SavedViewMutationError extends Error {
  readonly status: number
  readonly code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'SavedViewMutationError'
    this.status = status
    this.code = code
  }
}

export function isPinLimitError(error: unknown, attemptedPin: boolean): boolean {
  return attemptedPin && error instanceof SavedViewMutationError && error.code === PINNED_LIMIT_EXCEEDED
}

export function pinnedMutationErrorMessage(
  error: unknown,
  attemptedPin: boolean,
  viewName: string,
  pinLimit: number = VALUES.maxPinnedViews,
): string {
  if (isPinLimitError(error, attemptedPin)) return LABELS.views.pinLimitReached(pinLimit)
  return attemptedPin ? LABELS.views.pinError(viewName) : LABELS.views.unpinError(viewName)
}

export async function sendSavedViewMutation(
  path: string,
  init: JSONRequestInit,
  errorMessage: string,
): Promise<Response> {
  const response = await verifiedFetchJSON(path, init)
  if (!response.ok) {
    const {message, code} = await parseFetchError(response, errorMessage)
    throw new SavedViewMutationError(response.status, message, code)
  }
  return response
}

export async function sendSavedViewMutationJson<T>(
  path: string,
  init: JSONRequestInit,
  errorMessage: string,
): Promise<T> {
  const response = await sendSavedViewMutation(path, init, errorMessage)
  return (await response.json()) as T
}

export function primeSavedViewCache({queryClient, owner, repo, basePath}: SavedViewCacheTargets, savedView: SavedView) {
  const detailKey = savedViewQueryKey({owner, repo, basePath, id: savedView.id})
  const existing = queryClient.getQueryData<SavedViewDetailCache>(detailKey)
  queryClient.setQueryData<SavedViewDetailCache>(detailKey, {
    savedView: preserveViewId(existing?.savedView, savedView),
    viewerCanManageViews: existing?.viewerCanManageViews ?? true,
  })
}

export function primeSavedViewCaches(targets: SavedViewCacheTargets, savedView: SavedView): void {
  primeSavedViewCache(targets, savedView)
  const listKey = savedViewsQueryKey(targets)
  targets.queryClient.setQueryData<SavedViewsResponse>(listKey, current => {
    if (!current) return current

    const savedViewExists = current.savedViews.some(view => String(view.id) === String(savedView.id))
    return {
      ...current,
      savedViews: savedViewExists
        ? current.savedViews.map(view =>
            String(view.id) === String(savedView.id) ? preserveViewId(view, savedView) : view,
          )
        : [...current.savedViews, savedView],
    }
  })
  invalidateSavedViewsList(targets)
}

export function invalidateSavedViewsList({queryClient, owner, repo, listUrl}: SavedViewCacheTargets): void {
  void queryClient.invalidateQueries({queryKey: savedViewsQueryKey({owner, repo, listUrl}), exact: true})
  void queryClient.invalidateQueries({queryKey: savedViewPinsQueryKey({owner, repo, listUrl}), exact: true})
}

export function invalidateSavedView({queryClient, owner, repo, basePath}: SavedViewCacheTargets, id: SavedViewId) {
  void queryClient.invalidateQueries({queryKey: savedViewQueryKey({owner, repo, basePath, id}), exact: true})
}

/** Cancels any in-flight `/:id.json` read, so a response from before a write cannot replace the written view. */
export async function cancelSavedViewRead(
  {queryClient, owner, repo, basePath}: SavedViewCacheTargets,
  id: number | string,
): Promise<void> {
  await queryClient.cancelQueries({queryKey: savedViewQueryKey({owner, repo, basePath, id}), exact: true})
}

export function removeSavedViewCaches(targets: SavedViewCacheTargets, id: number | string): void {
  const {queryClient, owner, repo, basePath, listUrl} = targets
  queryClient.removeQueries({queryKey: savedViewQueryKey({owner, repo, basePath, id}), exact: true})
  queryClient.setQueryData<SavedView[]>(savedViewPinsQueryKey({owner, repo, listUrl}), current =>
    current?.some(view => String(view.id) === String(id))
      ? current.filter(view => String(view.id) !== String(id))
      : current,
  )
  invalidateSavedViewsList(targets)
}

export interface SavedViewPinnedSnapshot {
  id: SavedViewId
  pinned: boolean
  listKey: readonly unknown[]
  detailKey: readonly unknown[]
  pinnedViewsKey: readonly unknown[]
  optimisticPinnedViews?: SavedView[]
  list?: {previousPinned: boolean | undefined}
  detail?: {previousPinned: boolean | undefined}
  pinnedViews?: {
    row: SavedView
    index: number
    wasPinned: boolean
  }
}

export async function optimisticallySetSavedViewPinned(
  {queryClient, owner, repo, basePath, listUrl}: SavedViewCacheTargets,
  {id, pinned, view}: SetSavedViewPinnedVariables,
): Promise<SavedViewPinnedSnapshot> {
  const listKey = savedViewsQueryKey({owner, repo, listUrl})
  const detailKey = savedViewQueryKey({owner, repo, basePath, id})
  const pinnedViewsKey = savedViewPinsQueryKey({owner, repo, listUrl})

  const hasCachedList = queryClient.getQueryData<SavedViewsResponse>(listKey) !== undefined
  const hasCachedDetails = queryClient.getQueryData<SavedViewDetailCache>(detailKey) !== undefined
  const hasCachedPinnedViews = queryClient.getQueryData<SavedView[]>(pinnedViewsKey) !== undefined

  await Promise.all([
    hasCachedList ? queryClient.cancelQueries({queryKey: listKey, exact: true}) : undefined,
    hasCachedDetails ? queryClient.cancelQueries({queryKey: detailKey, exact: true}) : undefined,
    hasCachedPinnedViews ? queryClient.cancelQueries({queryKey: pinnedViewsKey, exact: true}) : undefined,
  ])

  const snapshot: SavedViewPinnedSnapshot = {id, pinned, listKey, detailKey, pinnedViewsKey}

  if (hasCachedList) {
    queryClient.setQueryData<SavedViewsResponse>(listKey, current => {
      if (!current) return current
      const target = current.savedViews.find(candidate => String(candidate.id) === String(id))
      if (!target) return current
      snapshot.list = {previousPinned: target.pinned}
      if (target.pinned === pinned) return current
      return {
        ...current,
        savedViews: current.savedViews.map(candidate =>
          String(candidate.id) === String(id) ? {...candidate, pinned} : candidate,
        ),
      }
    })
  }

  if (hasCachedDetails) {
    queryClient.setQueryData<SavedViewDetailCache>(detailKey, current => {
      if (!current || String(current.savedView.id) !== String(id)) return current
      snapshot.detail = {previousPinned: current.savedView.pinned}
      if (current.savedView.pinned === pinned) return current
      return {...current, savedView: {...current.savedView, pinned}}
    })
  }

  if (hasCachedPinnedViews) {
    queryClient.setQueryData<SavedView[]>(pinnedViewsKey, current => {
      if (!current) return current
      const index = current.findIndex(candidate => String(candidate.id) === String(id))
      const cachedPinnedView = index >= 0 ? current[index] : undefined
      const detailRow = queryClient.getQueryData<SavedViewDetailCache>(detailKey)?.savedView
      const listRow = queryClient
        .getQueryData<SavedViewsResponse>(listKey)
        ?.savedViews.find(candidate => String(candidate.id) === String(id))
      const target = [cachedPinnedView, detailRow, listRow, view].find(
        candidate => candidate && String(candidate.id) === String(id),
      )
      if (!target) return current

      snapshot.pinnedViews = {row: target, index, wasPinned: index >= 0}
      if (pinned && index >= 0) {
        if (cachedPinnedView?.pinned === true) return current
        return current.map(candidate =>
          String(candidate.id) === String(id) ? {...candidate, pinned: true} : candidate,
        )
      }
      if (!pinned && index < 0) return current
      if (!pinned) return current.filter(candidate => String(candidate.id) !== String(id))

      const row = {...target, pinned: true}
      const remaining = current.filter(candidate => String(candidate.id) !== String(id))
      const insertAt =
        viewVisibility(target) === 'personal'
          ? 0
          : remaining.findIndex(candidate => viewVisibility(candidate) === 'team')
      remaining.splice(insertAt < 0 ? remaining.length : insertAt, 0, row)
      return remaining
    })
  }

  snapshot.optimisticPinnedViews = queryClient.getQueryData<SavedView[]>(pinnedViewsKey)
  return snapshot
}

export function restoreSavedViewPinnedSnapshot(
  {queryClient}: SavedViewCacheTargets,
  snapshot: SavedViewPinnedSnapshot,
): void {
  const {id, pinned} = snapshot
  if (snapshot.list) {
    const {previousPinned} = snapshot.list
    queryClient.setQueryData<SavedViewsResponse>(snapshot.listKey, current =>
      current
        ? {
            ...current,
            savedViews: current.savedViews.map(view =>
              String(view.id) === String(id) && view.pinned === pinned ? {...view, pinned: previousPinned} : view,
            ),
          }
        : current,
    )
  }
  if (snapshot.detail) {
    const {previousPinned} = snapshot.detail
    queryClient.setQueryData<SavedViewDetailCache>(snapshot.detailKey, current =>
      current && String(current.savedView.id) === String(id) && current.savedView.pinned === pinned
        ? {...current, savedView: {...current.savedView, pinned: previousPinned}}
        : current,
    )
  }
  if (snapshot.pinnedViews) {
    const {row, index, wasPinned} = snapshot.pinnedViews
    queryClient.setQueryData<SavedView[]>(snapshot.pinnedViewsKey, current => {
      if (!current) return current
      const currentIndex = current.findIndex(candidate => String(candidate.id) === String(id))
      if (wasPinned && currentIndex < 0) {
        const pinnedViews = [...current]
        pinnedViews.splice(Math.min(index, pinnedViews.length), 0, row)
        return pinnedViews
      }
      if (!wasPinned && currentIndex >= 0 && current[currentIndex]?.pinned === pinned) {
        return current.filter(candidate => String(candidate.id) !== String(id))
      }
      return current
    })
  }
}
